import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase } from '../services/supabaseClient';
import { notificationService } from '../services/notificationService';

const NotificationsInboxContext = createContext(null);

/** Page size for GET /api/user/notifications (matches Explore “More kits” batch size). */
export const NOTIFICATIONS_PAGE_SIZE = 8;

/** Cap in-memory list when prepending realtime rows (optional bound). */
const MAX_ITEMS_IN_MEMORY = 50;

/** Treat as read only when server set read_at (ISO string). */
export function isNotificationUnread(n) {
  return n == null || n.read_at == null || n.read_at === '';
}

export function NotificationsInboxProvider({ user, children }) {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  const itemsRef = useRef(items);
  const hasMoreRef = useRef(hasMore);
  const loadingMoreRef = useRef(false);
  itemsRef.current = items;
  hasMoreRef.current = hasMore;
  loadingMoreRef.current = loadingMore;

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const canRealtime = supabase && token && token !== 'authenticated_via_cookie';

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await notificationService.list({ limit: NOTIFICATIONS_PAGE_SIZE });
      const list = data.notifications || [];
      setItems(list);
      setHasMore(!!data.has_more);
      setUnreadCount(typeof data.unread_count === 'number' ? data.unread_count : 0);
    } catch (e) {
      setError(e.message || 'Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const loadMore = useCallback(async () => {
    if (!user?.id || loadingMoreRef.current || !hasMoreRef.current) return;
    const last = itemsRef.current[itemsRef.current.length - 1];
    if (!last?.id) return;

    setLoadingMore(true);
    setError('');
    try {
      const data = await notificationService.list({
        limit: NOTIFICATIONS_PAGE_SIZE,
        before: last.id,
      });
      const page = data.notifications || [];
      setItems((prev) => {
        const seen = new Set(prev.map((x) => String(x.id)));
        const merged = [...prev];
        for (const n of page) {
          const id = String(n.id);
          if (!seen.has(id)) {
            seen.add(id);
            merged.push(n);
          }
        }
        return merged;
      });
      setHasMore(!!data.has_more);
      if (typeof data.unread_count === 'number') {
        setUnreadCount(data.unread_count);
      }
    } catch (e) {
      setError(e.message || 'Could not load more notifications');
    } finally {
      setLoadingMore(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return undefined;
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [user?.id, load]);

  useEffect(() => {
    if (!canRealtime || !user?.id) return undefined;

    try {
      if (typeof supabase.realtime?.setAuth === 'function') {
        supabase.realtime.setAuth(token);
      }
    } catch (e) {
      /* ignore */
    }

    const channel = supabase
      .channel(`user_notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new;
          if (!row) return;
          if (isNotificationUnread(row)) {
            setUnreadCount((c) => c + 1);
          }
          setItems((prev) => {
            if (prev.some((p) => String(p.id) === String(row.id))) return prev;
            return [row, ...prev].slice(0, MAX_ITEMS_IN_MEMORY);
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new;
          const oldRow = payload.old;
          if (!row) return;
          if (
            oldRow &&
            isNotificationUnread(oldRow) &&
            !isNotificationUnread(row)
          ) {
            setUnreadCount((c) => Math.max(0, c - 1));
          }
          setItems((prev) =>
            prev.map((x) =>
              String(x.id) === String(row.id) ? { ...x, ...row, read_at: row.read_at } : x
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [canRealtime, user?.id, token]);

  /**
   * @param {string} id
   * @param {string} readAtIso
   * @param {boolean} [decrementUnread] — when true, decrease badge (optimistic single read)
   */
  const applyMarkRead = useCallback((id, readAtIso, decrementUnread = false) => {
    const ts = readAtIso || new Date().toISOString();
    setItems((prev) =>
      prev.map((x) => (String(x.id) === String(id) ? { ...x, read_at: ts } : x))
    );
    if (decrementUnread) {
      setUnreadCount((c) => Math.max(0, c - 1));
    }
  }, []);

  const applyMarkAllRead = useCallback((readAtIso) => {
    const ts = readAtIso || new Date().toISOString();
    setUnreadCount(0);
    setItems((prev) => prev.map((x) => (isNotificationUnread(x) ? { ...x, read_at: ts } : x)));
  }, []);

  const value = useMemo(
    () => ({
      items,
      setItems,
      unreadCount,
      hasMore,
      loading,
      loadingMore,
      error,
      load,
      loadMore,
      applyMarkRead,
      applyMarkAllRead,
      setError,
    }),
    [
      items,
      unreadCount,
      hasMore,
      loading,
      loadingMore,
      error,
      load,
      loadMore,
      applyMarkRead,
      applyMarkAllRead,
    ]
  );

  return (
    <NotificationsInboxContext.Provider value={value}>{children}</NotificationsInboxContext.Provider>
  );
}

export function useNotificationsInbox() {
  const ctx = useContext(NotificationsInboxContext);
  if (!ctx) {
    throw new Error('useNotificationsInbox must be used within NotificationsInboxProvider');
  }
  return ctx;
}
